<template>
  <ContentWrap>
    <el-alert type="info" :closable="false" show-icon class="mb-12px" title="本所开票台账：草稿 → 开具 → 可作废。关联本所 Matter。" />
    <el-form :inline="true" class="mb-12px" @submit.prevent>
      <el-form-item label="案件ID">
        <el-input-number v-model="query.caseId" :min="1" controls-position="right" class="!w-120px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px">
          <el-option label="草稿" value="DRAFT" />
          <el-option label="已开具" value="ISSUED" />
          <el-option label="作废" value="VOID" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button type="success" @click="openCreate">新建开票</el-button>
      </el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" border>
      <el-table-column prop="id" label="编号" width="70" />
      <el-table-column prop="firmCaseNo" label="案号" min-width="110" />
      <el-table-column prop="invoiceNo" label="发票号" min-width="120" />
      <el-table-column prop="amount" label="金额" width="100" />
      <el-table-column prop="invoiceDate" label="开票日" width="110" />
      <el-table-column prop="status" label="状态" width="90" />
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="{ row }">
          <el-button v-if="row.status === 'DRAFT'" link type="primary" @click="doIssue(row)">开具</el-button>
          <el-button v-if="row.status === 'ISSUED'" link type="danger" @click="doVoid(row)">作废</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination v-model:page="query.pageNo" v-model:limit="query.pageSize" :total="total" @pagination="getList" />
    <Dialog v-model="visible" title="新建开票" width="480px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="案件" prop="caseId">
          <el-select v-model="form.caseId" filterable class="!w-100%">
            <el-option v-for="m in matters" :key="m.id" :label="`${m.firmCaseNo || m.id} · ${m.title}`" :value="m.id!" />
          </el-select>
        </el-form-item>
        <el-form-item label="发票号" prop="invoiceNo"><el-input v-model="form.invoiceNo" /></el-form-item>
        <el-form-item label="金额" prop="amount"><el-input-number v-model="form.amount" :min="0.01" :precision="2" class="!w-100%" /></el-form-item>
        <el-form-item label="开票日" prop="invoiceDate">
          <el-date-picker v-model="form.invoiceDate" type="date" value-format="YYYY-MM-DD" class="!w-100%" />
        </el-form-item>
        <el-form-item label="标题"><el-input v-model="form.title" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存草稿</el-button>
      </template>
    </Dialog>
  </ContentWrap>
</template>
<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules } from 'element-plus'
import { FirmOfficeApi, type PracticeInvoiceVO } from '@/api/firm/office'
import { FirmPracticeApi, type PracticeMatterVO } from '@/api/firm/practice'
defineOptions({ name: 'FirmPracticeInvoice' })
const message = useMessage()
const loading = ref(false)
const list = ref<PracticeInvoiceVO[]>([])
const total = ref(0)
const query = reactive({ pageNo: 1, pageSize: 10, caseId: undefined as number | undefined, status: undefined as string | undefined })
const matters = ref<PracticeMatterVO[]>([])
const visible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({ caseId: undefined as number | undefined, invoiceNo: '', amount: 0, invoiceDate: '', title: '' })
const rules: FormRules = {
  caseId: [{ required: true, message: '请选择案件', trigger: 'change' }],
  invoiceNo: [{ required: true, message: '请输入发票号', trigger: 'blur' }],
  amount: [{ required: true, message: '请输入金额', trigger: 'blur' }],
  invoiceDate: [{ required: true, message: '请选择日期', trigger: 'change' }]
}
const getList = async () => {
  loading.value = true
  try {
    const data = await FirmOfficeApi.invoicePage(query)
    list.value = data?.list || []
    total.value = data?.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => { query.pageNo = 1; getList() }
const openCreate = async () => {
  Object.assign(form, { caseId: undefined, invoiceNo: '', amount: 0, invoiceDate: '', title: '' })
  visible.value = true
  const m = await FirmPracticeApi.matterPage({ pageNo: 1, pageSize: 100 })
  matters.value = m?.list || []
}
const submit = async () => {
  await formRef.value?.validate()
  if (!form.caseId) return
  saving.value = true
  try {
    await FirmOfficeApi.invoiceCreate({ ...form, caseId: form.caseId, title: form.title || undefined })
    message.success('已创建')
    visible.value = false
    await getList()
  } finally {
    saving.value = false
  }
}
const doIssue = async (row: PracticeInvoiceVO) => {
  if (!row.id) return
  await FirmOfficeApi.invoiceIssue(row.id)
  message.success('已开具')
  await getList()
}
const doVoid = async (row: PracticeInvoiceVO) => {
  if (!row.id) return
  const voidReason = window.prompt('作废原因') || undefined
  await FirmOfficeApi.invoiceVoid({ id: row.id, voidReason })
  message.success('已作废')
  await getList()
}
onMounted(getList)
</script>
